import React, { useState, useEffect } from 'react';
import { Select, MenuItem, FormControl, InputLabel, Typography } from '@mui/material';
import WidgetWrapper from './WidgetWrapper';
import tagService from '../services/tagService';
import { useWorkflowExecution } from '../hooks/useWorkflowExecution';

const ComboBox = ({ 
  label = '',
  options = [], // Array of {value, label} objects or simple string array
  selectedValue = '',
  placeholder = 'Select option',
  disabled = false,
  variant = 'outlined', // 'outlined', 'filled', 'standard'
  size = 'medium', // 'small', 'medium'
  tagId = null,
  tagValue = null, // Will be injected by preview mode
  onChange,
  // Workflow execution properties
  enableWorkflow = false,
  workflowName = '',
  workflowParameters = {},
  companyId = 1,
  enableLogging = false,
  watchdogTimeout = null,
  isEditMode = false,
  // Style properties
  backgroundColor,
  foreColor,
  borderWidth,
  borderColor,
  borderRadius,
  fontSize,
  fontWeight,
  fontFamily,
  opacity,
  ...props 
}) => {
  const { executeWorkflow, isExecuting } = useWorkflowExecution();

  // Use tag value if available, otherwise use static value
  const currentValue = tagValue !== null && tagValue !== undefined ? tagValue : selectedValue;
  const [localValue, setLocalValue] = useState(currentValue);

  useEffect(() => {
    setLocalValue(currentValue);
  }, [currentValue]);

  // Normalize options to {value, label} format
  const normalizedOptions = options.map(option => 
    typeof option === 'string' ? { value: option, label: option } : option
  );

  const handleChange = async (event) => {
    const newValue = event.target.value;
    setLocalValue(newValue);

    // Skip actions in edit mode
    if (isEditMode) {
      return;
    }

    // Execute workflow if enabled
    if (enableWorkflow && workflowName) {
      try {
        // Add selected value to workflow parameters
        const parameters = {
          ...workflowParameters,
          selectedValue: newValue,
          comboValue: newValue
        };

        await executeWorkflow(
          workflowName,
          parameters,
          companyId,
          enableLogging,
          watchdogTimeout
        );


        // Show success notification
        if (window.showNotification) {
          window.showNotification(`Workflow "${workflowName}" completed successfully`, 'success');
        }

      } catch (error) {
        console.error('Failed to execute combobox workflow:', error);

        // Revert combobox value on workflow failure
        setLocalValue(currentValue);

        // Show error notification
        if (window.showNotification) {
          window.showNotification(`Failed to execute workflow "${workflowName}": ${error.message}`, 'error');
        } else {
          alert(`Failed to execute workflow "${workflowName}": ${error.message}`);
        }
        return;
      }
    }

    // Update tag if connected
    if (tagId) {
      try {
        await tagService.updateTagValue(tagId, newValue);
      } catch (error) {
        console.error('Failed to update tag value:', error);
        // Revert on error
        setLocalValue(currentValue);
      }
    }

    // Call external onChange if provided
    if (onChange) {
      onChange(newValue);
    }
  };

  return (
    <WidgetWrapper 
      backgroundColor={backgroundColor}
      foreColor={foreColor}
      borderWidth={borderWidth}
      borderColor={borderColor}
      borderRadius={borderRadius}
      fontSize={fontSize}
      fontWeight={fontWeight}
      fontFamily={fontFamily}
      opacity={opacity}
      justifyContent="stretch"
      alignItems="stretch"
      padding="8px"
      {...props}
    >
      <FormControl
        fullWidth
        variant={variant}
        size={size}
        disabled={disabled || isExecuting || isEditMode}
        sx={{
          '& .MuiOutlinedInput-root': {
            color: 'inherit',
            borderRadius: borderRadius ? `${borderRadius}px` : undefined,
            '& fieldset': {
              borderColor: borderColor || 'rgba(0, 0, 0, 0.23)',
              borderRadius: borderRadius ? `${borderRadius}px` : undefined
            },
            '&:hover fieldset': {
              borderColor: foreColor || undefined
            },
            '&.Mui-focused fieldset': {
              borderColor: foreColor || undefined
            }
          },
          '& .MuiInputLabel-root': {
            color: 'inherit',
            '&.Mui-focused': {
              color: foreColor || undefined
            }
          },
          '& .MuiSelect-select': {
            color: 'inherit'
          }
        }}
      >
        {label && (
          <InputLabel 
            sx={{ 
              fontSize: 'inherit',
              fontWeight: 'inherit',
              fontFamily: 'inherit'
            }}
          >
            {label}
          </InputLabel>
        )}
        <Select
          value={localValue}
          onChange={handleChange}
          label={label}
          displayEmpty={!!placeholder}
          sx={{
            fontSize: 'inherit',
            fontWeight: 'inherit',
            fontFamily: 'inherit',
            color: 'inherit'
          }}
        >
          {placeholder && !label && (
            <MenuItem value="" disabled>
              <Typography 
                sx={{ 
                  color: 'text.secondary',
                  fontSize: 'inherit',
                  fontWeight: 'inherit',
                  fontFamily: 'inherit'
                }}
              >
                {placeholder}
              </Typography>
            </MenuItem>
          )}
          {normalizedOptions.map((option, index) => (
            <MenuItem key={index} value={option.value}>
              <Typography
                sx={{
                  fontSize: 'inherit',
                  fontWeight: 'inherit', 
                  fontFamily: 'inherit'
                }}
              >
                {option.label}
              </Typography>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </WidgetWrapper>
  );
};

export default ComboBox;